import type { MemoBlock, MemoEntryKind } from "@/lib/types";
import { buildMemoSections } from "@/lib/memoSections";

/** An Abschnitt and the Zwischenabschnitte a point can be moved into. */
export interface MemoMoveSection {
  id: string;
  title: string;
  /** OPEN sub-headings only, in render order. A closed one takes no points: it drops at
   *  carry-forward while an open member carries on (the server refuses it too). */
  subheadings: { id: string; title: string }[];
}

/** One row of the "Verschieben nach" menu. */
export interface MemoMoveTarget {
  blockId: string;
  /**
   * - `undefined` → the Abschnitt's END (a Zwischenüberschrift moving with its points; the
   *   request keeps the legacy `{block_id}` shape),
   * - `null` → the Abschnitt's TOP LEVEL: the run before its first Zwischenüberschrift,
   * - an id → the end of that Zwischenabschnitt's points.
   */
  subheadingId?: string | null;
  label: string;
  /** A Zwischenabschnitt, indented under its Abschnitt. */
  nested: boolean;
  /** Where the point already is: shown for orientation, never offered. */
  current?: boolean;
}

/** The move menu's view of the memo — the SAME walk the page renders with
 *  (`buildMemoSections`), so a heading is listed exactly where it appears. */
export function memoMoveSections(blocks: MemoBlock[]): MemoMoveSection[] {
  return blocks.map((b) => ({
    id: b.id,
    title: b.title,
    subheadings: buildMemoSections(b)
      .map((s) => s.heading)
      .filter((h) => h !== null && !h.retired)
      .map((h) => ({ id: h!.id, title: h!.body })),
  }));
}

/**
 * The menu rows for one entry.
 *
 * A POINT is offered every Abschnitt (its top level) with that Abschnitt's Zwischenabschnitte
 * beneath it — its OWN Abschnitt included, because moving between two Zwischenabschnitte of
 * one Abschnitt is the point of this menu. Its current section is kept as a `current` row so
 * the list says where it is, but is never a target.
 *
 * A ZWISCHENÜBERSCHRIFT keeps the old behaviour: only the OTHER Abschnitte, as a whole — the
 * heading moves with its points to the target's end. An update never moves on its own.
 */
export function memoMoveTargets(
  sections: MemoMoveSection[],
  at: { kind: MemoEntryKind; blockId: string; headingId: string | null },
): MemoMoveTarget[] {
  if (at.kind === "update") return [];
  if (at.kind === "subheading") {
    return sections
      .filter((s) => s.id !== at.blockId)
      .map((s) => ({ blockId: s.id, label: s.title, nested: false }));
  }
  return sections.flatMap((s) => {
    const here = s.id === at.blockId;
    return [
      {
        blockId: s.id,
        subheadingId: null,
        label: s.title,
        nested: false,
        current: here && at.headingId === null,
      },
      ...s.subheadings.map((h) => ({
        blockId: s.id,
        subheadingId: h.id,
        label: h.title,
        nested: true,
        current: here && at.headingId === h.id,
      })),
    ];
  });
}
